import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LogIn, KeyRound, Clock, Building2, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { changeExpiredPassword } from '../../services/authService';
import TextInput from '../../components/ui/TextInput';
import LanguagePicker from '../../components/LanguagePicker';
import PasswordPolicyChecklist from '../../components/PasswordPolicyChecklist';
import { isPasswordAllowed } from '../../utils/passwordPolicy';
import flexbeeLogo from '../../assets/media/flexbee-no-bg.webp';
import Mascot from '../../components/Mascot';
import useLoginMascot from './useLoginMascot';
import MfaLoginStep from './MfaLoginStep';
import SandboxLoginNotice from './SandboxLoginNotice';
import { isSandboxEnvironment, ssoStartUrl } from '../../utils/environment';
import { discoverSso, SSO_MODES } from '../../services/ssoService';
import { createPkcePair, saveVerifier, saveRedirect } from '../../utils/ssoPkce';
import {
  PageWrapper,
  ThirdOrb,
  GridOverlay,
  LanguagePickerWrapper,
  FormCard,
  LogoContainer,
  FormTitle,
  FormSubtitle,
  Form,
  RememberRow,
  RememberLabel,
  ForgotLink,
  GradientButton,
  MascotLayer,
  BackButton,
  InfoMessage,
  FirstLoginRow,
} from './styles';

// Sunucu süresi dolmuş şifreyle oturum AÇMAZ: 403 `auth.passwordExpired` + gövdede politika.
export const isPasswordExpiredError = (err) =>
  err?.response?.status === 403 && err?.response?.data?.message === 'auth.passwordExpired';

// Şifre doğru; sunucu iki adımlı doğrulama istiyor (kod ya da kurulum). Gövde = sınama.
export const isMfaChallengeError = (err) =>
  err?.response?.status === 403
  && ['auth.mfaRequired', 'auth.mfaEnrollmentRequired'].includes(err?.response?.data?.message)
  && Boolean(err?.response?.data?.data?.challengeToken);

export default function LoginPage({ history }) {
  const { t } = useTranslation();
  const { login, adoptSession } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const mascot = useLoginMascot();

  // Şifresi dolmuş kullanıcı: aynı kartta yeni şifre formu. `expiredPolicy` doluyken form görünür.
  const [expiredPolicy, setExpiredPolicy] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [expiredError, setExpiredError] = useState('');

  // İki adımlı doğrulama sınaması (doluyken giriş kartı MFA adımını gösterir).
  const [mfaChallenge, setMfaChallenge] = useState(null);

  // Kurumsal giriş (spec §5.1): önce e-posta → alan adı keşfi. ssoMode null = keşif yapılmadı (yalnız e-posta adımı).
  // 'none' → parola; 'optional' → kurumsal düğme + "Parola ile Giriş"; 'required' → kurumsal düğme + acil durum bağlantısı.
  const [ssoMode, setSsoMode] = useState(null);
  // `/login?method=password` (SSO hata sayfasının "Parola ile Giriş" bağlantısı): keşiften sonra parola alanı doğrudan açılır.
  const [showPassword, setShowPassword] = useState(() => new URLSearchParams(history.location.search).get('method') === 'password');
  const [discovering, setDiscovering] = useState(false);

  // Hareketsizlik zaman aşımıyla gelindi (AuthContext.logoutForIdle → /login?reason=idle).
  const idleReason = new URLSearchParams(history.location.search).get('reason') === 'idle';

  const discover = async (address) => {
    const value = (address || '').trim();
    if (!value) return;
    setDiscovering(true);
    try {
      const res = await discoverSso(value);
      const mode = res?.data?.data?.mode;
      setSsoMode(Object.values(SSO_MODES).includes(mode) ? mode : SSO_MODES.NONE);
    } catch {
      setSsoMode(SSO_MODES.NONE); // keşif ucu erişilemezse parola yolu açık kalır
    } finally {
      setDiscovering(false);
    }
  };

  // Hatırlanan e-posta: alan adı keşfi kendiliğinden yapılır (kurumsal kullanıcı tek tıkla devam eder).
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('flexbee_remember') || 'null');
      if (saved?.email) {
        setEmail(saved.email);
        setRememberMe(true);
        discover(saved.email);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetEmailStep = () => {
    setSsoMode(null);
    setShowPassword(false);
    setPassword('');
  };

  const startSso = async () => {
    setLoading(true);
    try {
      const { verifier, challenge } = await createPkcePair();
      saveVerifier(verifier);
      saveRedirect(new URLSearchParams(history.location.search).get('redirect'));
      window.location.assign(ssoStartUrl({ email: email.trim(), challenge }));
    } catch {
      setLoading(false);
    }
  };

  const passwordVisible = ssoMode === SSO_MODES.NONE || showPassword;

  const redirectAfterLogin = async () => {
    mascot.notifySubmit('success');
    // Maskotun "sevinme" animasyonunun (≈0.6s) görünmesi için kısa bir bekleme, sonra yönlendir.
    // prefers-reduced-motion açıksa animasyon zaten oynamıyor → gereksiz gecikme yapma.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!prefersReducedMotion) {
      await new Promise((resolve) => setTimeout(resolve, 600));
    }
    const params = new URLSearchParams(history.location.search);
    const redirect = params.get('redirect');
    history.push(redirect || '/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordVisible) {        // e-posta adımı: Devam = keşif
      await discover(email);
      return;
    }
    setLoading(true);
    try {
      await login({ email, password, rememberMe });
      await redirectAfterLogin();
    } catch (err) {
      if (isMfaChallengeError(err)) {
        setMfaChallenge(err.response.data.data);
      } else if (isPasswordExpiredError(err)) {
        setExpiredPolicy(err.response.data?.data || {});
        setNewPassword(''); setConfirmPassword(''); setExpiredError('');
      } else if (err?.response?.data?.message === 'auth.sso.required') {
        // Keşif bayat olabilir (zorunluluk az önce açıldı): kurumsal düğmeye geç.
        setSsoMode(SSO_MODES.REQUIRED);
        setShowPassword(false);
        setPassword('');
      } else {
        // Error toast handled by interceptor
        mascot.notifySubmit('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExpiredSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordAllowed(newPassword, expiredPolicy)) {
      setExpiredError(t('passwordPolicy.notMet'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setExpiredError(t('setPassword.mismatch'));
      return;
    }

    setLoading(true);
    setExpiredError('');
    try {
      await changeExpiredPassword({
        email,
        currentPassword: password,
        newPassword,
        confirmPassword,
      });
      // Tek oturum açma yolu Login'dir (lisans kapısı, cookie, giriş sayacı oradan geçer).
      await login({ email, password: newPassword, rememberMe });
      setExpiredPolicy(null);
      await redirectAfterLogin();
    } catch (err) {
      // Yeni şifre kaydedildi; giriş MFA isteyebilir → aynı kartta MFA adımı.
      if (isMfaChallengeError(err)) {
        setExpiredPolicy(null);
        setMfaChallenge(err.response.data.data);
      }
      // Diğer hatalar: toast interceptor'da.
    } finally {
      setLoading(false);
    }
  };

  const completeMfa = async (sessionUser) => {
    adoptSession(sessionUser, { email, rememberMe });
    setMfaChallenge(null);
    await redirectAfterLogin();
  };

  const backFromMfa = () => {
    setMfaChallenge(null);
    setPassword('');
  };

  const backToLogin = () => {
    setExpiredPolicy(null);
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setExpiredError('');
  };

  // Test ortamı (REACT_APP_ENVIRONMENT=sandbox): parola formu yok — canlı panelden girilir.
  // Kanca çağrılarından SONRA döner (kanca sırası her iki kipte aynı kalır).
  if (isSandboxEnvironment()) return <SandboxLoginNotice idleReason={idleReason} />;

  if (mfaChallenge) {
    return (
      <PageWrapper>
        <ThirdOrb />
        <GridOverlay />
        <LanguagePickerWrapper>
          <LanguagePicker />
        </LanguagePickerWrapper>
        <FormCard>
          <LogoContainer>
            <img src={flexbeeLogo} alt="Flexbee" />
          </LogoContainer>
          <MfaLoginStep
            challenge={mfaChallenge}
            credentials={{ email, rememberMe }}
            onComplete={completeMfa}
            onBack={backFromMfa}
          />
        </FormCard>
      </PageWrapper>
    );
  }

  if (expiredPolicy) {
    return (
      <PageWrapper>
        <ThirdOrb />
        <GridOverlay />
        <LanguagePickerWrapper>
          <LanguagePicker />
        </LanguagePickerWrapper>
        <FormCard>
          <LogoContainer>
            <img src={flexbeeLogo} alt="Flexbee" />
          </LogoContainer>
          <FormTitle>{t('login.expired.title')}</FormTitle>
          <FormSubtitle>{t('login.expired.subtitle')}</FormSubtitle>
          <Form onSubmit={handleExpiredSubmit}>
            <TextInput
              label={t('setPassword.newPassword')}
              type="password"
              name="newPassword"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setExpiredError(''); }}
              required
              autoFocus
            />
            <TextInput
              label={t('setPassword.confirmPassword')}
              type="password"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setExpiredError(''); }}
              required
              error={expiredError}
            />
            <PasswordPolicyChecklist password={newPassword} policy={expiredPolicy} tone="dark" />
            <GradientButton type="submit" disabled={loading}>
              <KeyRound size={18} />
              {loading ? t('common.loading') : t('login.expired.submit')}
            </GradientButton>
          </Form>
          <BackButton type="button" onClick={backToLogin}>
            {t('login.expired.back')}
          </BackButton>
        </FormCard>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ThirdOrb />
      <GridOverlay />
      <LanguagePickerWrapper>
        <LanguagePicker />
      </LanguagePickerWrapper>
      <FormCard ref={mascot.containerRef}>
        <LogoContainer>
          <img src={flexbeeLogo} alt="Flexbee" />
        </LogoContainer>
        <FormTitle>{t('login.welcome')}</FormTitle>
        <FormSubtitle>{t('login.subtitle')}</FormSubtitle>
        {idleReason && (
          <InfoMessage role="status" data-testid="idle-logout-notice">
            <Clock size={16} />
            <span>{t('session.idleLoggedOut')}</span>
          </InfoMessage>
        )}
        <MascotLayer style={mascot.mascotStyle}>
          <Mascot mood={mascot.mood} lookX={mascot.lookX} />
        </MascotLayer>
        <Form onSubmit={handleSubmit} onFocus={mascot.formHandlers.onFocus} onBlur={mascot.formHandlers.onBlur}>
          <TextInput
            label={t('login.email')}
            type="email"
            name="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); mascot.onFieldType(e.target.value); if (ssoMode) resetEmailStep(); }}
            required
            autoFocus
          />

          {ssoMode === null && (
            <GradientButton type="submit" disabled={discovering || !email.trim()} data-testid="login-continue">
              <ArrowRight size={18} />
              {discovering ? t('common.loading') : t('login.sso.continue')}
            </GradientButton>
          )}

          {(ssoMode === SSO_MODES.OPTIONAL || ssoMode === SSO_MODES.REQUIRED) && !showPassword && (
            <>
              <GradientButton type="button" onClick={startSso} disabled={loading} data-testid="login-sso" data-login-action>
                <Building2 size={18} />
                {loading ? t('common.loading') : t('login.sso.button')}
              </GradientButton>
              <BackButton type="button" onClick={() => setShowPassword(true)} data-testid="login-show-password" data-login-action>
                {ssoMode === SSO_MODES.REQUIRED ? t('login.sso.breakGlass') : t('login.sso.usePassword')}
              </BackButton>
            </>
          )}

          {passwordVisible && (
            <>
              {ssoMode === SSO_MODES.REQUIRED && (
                <InfoMessage role="status" data-testid="login-break-glass-notice">
                  <KeyRound size={16} />
                  <span>{t('login.sso.breakGlassNotice')}</span>
                </InfoMessage>
              )}
              <TextInput
                label={t('login.password')}
                type="password"
                name="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); mascot.onFieldType(e.target.value); }}
                onVisibilityChange={mascot.onPasswordVisibilityChange}
                required
                autoFocus={ssoMode !== SSO_MODES.NONE}
              />
              <RememberRow>
                <RememberLabel>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  {t('login.rememberMe')}
                </RememberLabel>
                {ssoMode !== SSO_MODES.REQUIRED && (
                  <ForgotLink>
                    <Link to="/forgot-password">{t('login.forgotPassword')}</Link>
                  </ForgotLink>
                )}
              </RememberRow>
              <GradientButton type="submit" disabled={loading}>
                <LogIn size={18} />
                {loading ? t('common.loading') : t('login.loginButton')}
              </GradientButton>
            </>
          )}

          {ssoMode !== null && (
            <BackButton type="button" onClick={resetEmailStep} data-testid="login-change-email" data-login-action>
              {t('login.sso.changeEmail')}
            </BackButton>
          )}

          {/* İlk giriş: "Şifremi Unuttum"a gitmeden şifre belirleme — e-posta adımından itibaren görünür. */}
          {ssoMode !== SSO_MODES.REQUIRED && (
            <FirstLoginRow data-testid="login-first-login">
              {t('login.firstLogin')}{' '}
              <Link to="/create-password">{t('login.createPassword')}</Link>
            </FirstLoginRow>
          )}
        </Form>
      </FormCard>
    </PageWrapper>
  );
}
